/**
 * SVG path transforms for measurement.
 * - Cut length: relative absolute→transform→relative round-trip (transformPathDataWithMatrix).
 * - Selection bbox: absolute path transform only (transformPathDataAbsolute).
 */

export type SvgMatrixLike = { a: number; b: number; c: number; d: number; e: number; f: number };

type Point = { x: number; y: number };
type PathCommand = { letter: string; args: number[] };

const COMMAND_RE = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g;
const NUMBER_RE = /-?(?:\d*\.\d+|\d+)(?:[eE][-+]?\d+)?/g;

function isRelative(letter: string): boolean {
  return letter === letter.toLowerCase() && letter !== 'z';
}

function isMove(letter: string): boolean {
  return letter === 'M' || letter === 'm';
}

function isClose(letter: string): boolean {
  return letter === 'Z' || letter === 'z';
}

function isHorzVert(letter: string): boolean {
  return letter === 'H' || letter === 'h' || letter === 'V' || letter === 'v';
}

function paramCount(letter: string): number {
  switch (letter) {
    case 'M':
    case 'm':
    case 'L':
    case 'l':
    case 'T':
    case 't':
      return 2;
    case 'H':
    case 'h':
    case 'V':
    case 'v':
      return 1;
    case 'C':
    case 'c':
    case 'S':
    case 's':
      return 6;
    case 'Q':
    case 'q':
      return 4;
    case 'A':
    case 'a':
      return 7;
    case 'Z':
    case 'z':
      return 0;
    default:
      return 0;
  }
}

function add(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y };
}

function applyMatrix(matrix: SvgMatrixLike, point: Point): Point {
  return {
    x: matrix.a * point.x + matrix.c * point.y + matrix.e,
    y: matrix.b * point.x + matrix.d * point.y + matrix.f,
  };
}

function commandEndPoint(letter: string, args: number[], previous: Point, first: Point): Point {
  switch (letter) {
    case 'M':
      return { x: args[0], y: args[1] };
    case 'm':
      return add(previous, { x: args[0], y: args[1] });
    case 'L':
      return { x: args[0], y: args[1] };
    case 'l':
      return add(previous, { x: args[0], y: args[1] });
    case 'H':
      return { x: args[0], y: previous.y };
    case 'h':
      return { x: previous.x + args[0], y: previous.y };
    case 'V':
      return { x: previous.x, y: args[0] };
    case 'v':
      return { x: previous.x, y: previous.y + args[0] };
    case 'C':
      return { x: args[4], y: args[5] };
    case 'c':
      return add(previous, { x: args[4], y: args[5] });
    case 'Z':
    case 'z':
      return first;
    default:
      return previous;
  }
}

function parsePathCommands(pathData: string): PathCommand[] {
  const commands: PathCommand[] = [];
  COMMAND_RE.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = COMMAND_RE.exec(pathData)) !== null) {
    const letter = match[1];
    const numbers = (match[2].match(NUMBER_RE) || []).map(Number);
    const count = paramCount(letter);

    if (count === 0) {
      commands.push({ letter, args: [] });
      continue;
    }

    let i = 0;
    let currentLetter = letter;
    while (i < numbers.length) {
      commands.push({ letter: currentLetter, args: numbers.slice(i, i + count) });
      i += count;
      if (isMove(currentLetter)) {
        currentLetter = isRelative(currentLetter) ? 'l' : 'L';
      }
    }
  }

  return commands;
}

function horzVertToLine(command: PathCommand, previous: Point): PathCommand {
  const end = commandEndPoint(command.letter, command.args, previous, previous);
  if (isHorzVert(command.letter)) {
    return { letter: 'L', args: [end.x, end.y] };
  }
  return command;
}

function toAbsolute(command: PathCommand, previous: Point): PathCommand {
  const { letter, args } = command;
  if (!isRelative(letter) || isClose(letter)) return command;

  switch (letter) {
    case 'm':
      return { letter: 'M', args: [previous.x + args[0], previous.y + args[1]] };
    case 'l':
      return { letter: 'L', args: [previous.x + args[0], previous.y + args[1]] };
    case 'c': {
      const cp1 = add(previous, { x: args[0], y: args[1] });
      const cp2 = add(previous, { x: args[2], y: args[3] });
      const end = add(previous, { x: args[4], y: args[5] });
      return { letter: 'C', args: [cp1.x, cp1.y, cp2.x, cp2.y, end.x, end.y] };
    }
    case 'q': {
      const cp = add(previous, { x: args[0], y: args[1] });
      const end = add(previous, { x: args[2], y: args[3] });
      return { letter: 'Q', args: [cp.x, cp.y, end.x, end.y] };
    }
    case 's': {
      const cp2 = add(previous, { x: args[0], y: args[1] });
      const end = add(previous, { x: args[2], y: args[3] });
      return { letter: 'S', args: [cp2.x, cp2.y, end.x, end.y] };
    }
    case 't':
      return { letter: 'T', args: [previous.x + args[0], previous.y + args[1]] };
    case 'h':
      return { letter: 'H', args: [previous.x + args[0]] };
    case 'v':
      return { letter: 'V', args: [previous.y + args[0]] };
    default:
      return command;
  }
}

function transformAbsoluteCommand(command: PathCommand, matrix: SvgMatrixLike): PathCommand {
  if (isClose(command.letter)) return command;

  const { letter, args } = command;
  const nextArgs: number[] = [];

  if (letter === 'M' || letter === 'L') {
    const p = applyMatrix(matrix, { x: args[0], y: args[1] });
    nextArgs.push(p.x, p.y);
  } else if (letter === 'C' || letter === 'Q' || letter === 'S' || letter === 'T') {
    for (let i = 0; i < args.length; i += 2) {
      const p = applyMatrix(matrix, { x: args[i], y: args[i + 1] });
      nextArgs.push(p.x, p.y);
    }
  } else if (letter === 'H') {
    nextArgs.push(applyMatrix(matrix, { x: args[0], y: 0 }).x);
  } else if (letter === 'V') {
    nextArgs.push(applyMatrix(matrix, { x: 0, y: args[0] }).y);
  } else if (letter === 'A') {
    const end = applyMatrix(matrix, { x: args[5], y: args[6] });
    nextArgs.push(...args.slice(0, 5), end.x, end.y);
  } else {
    return command;
  }

  return { letter, args: nextArgs };
}

function toRelative(command: PathCommand, previous: Point): PathCommand {
  const { letter, args } = command;
  if (isClose(letter)) return { letter: 'z', args: [] };

  switch (letter) {
    case 'M': {
      const dx = args[0] - previous.x;
      const dy = args[1] - previous.y;
      return { letter: 'm', args: [dx, dy] };
    }
    case 'L': {
      const dx = args[0] - previous.x;
      const dy = args[1] - previous.y;
      return { letter: 'l', args: [dx, dy] };
    }
    case 'C': {
      const cp1 = { x: args[0] - previous.x, y: args[1] - previous.y };
      const cp2 = { x: args[2] - previous.x, y: args[3] - previous.y };
      const end = { x: args[4] - previous.x, y: args[5] - previous.y };
      return { letter: 'c', args: [cp1.x, cp1.y, cp2.x, cp2.y, end.x, end.y] };
    }
    default:
      return command;
  }
}

function formatNumber(value: number): string {
  const rounded = Math.abs(value) < 1e-10 ? 0 : value;
  return Number(rounded.toPrecision(8)).toString();
}

function serializeCommands(commands: PathCommand[]): string {
  return commands
    .map(({ letter, args }) => {
      if (args.length === 0) return letter;
      return `${letter} ${args.map(formatNumber).join(' ')}`;
    })
    .join(' ');
}

/** Transform path `d` with a matrix via absolute→transform→relative round-trip. */
export function transformPathDataWithMatrix(pathData: string, matrix: SvgMatrixLike): string {
  const commands = parsePathCommands(pathData);
  if (!commands.length) return '';

  const output: PathCommand[] = [];
  let previous: Point = { x: 0, y: 0 };
  let previousNew: Point = { x: 0, y: 0 };
  let first: Point = { x: 0, y: 0 };
  let firstNew: Point = { x: 0, y: 0 };
  let startZone = true;

  for (const command of commands) {
    let segment = command;
    if (startZone) {
      first = commandEndPoint(segment.letter, segment.args, previous, first);
    }

    if (isHorzVert(segment.letter)) {
      segment = horzVertToLine(segment, previous);
    }

    let transformed: PathCommand;
    if (isRelative(segment.letter)) {
      const absolute = toAbsolute(segment, previous);
      const absoluteTransformed = transformAbsoluteCommand(absolute, matrix);
      transformed = toRelative(absoluteTransformed, previousNew);
    } else {
      transformed = transformAbsoluteCommand(segment, matrix);
    }

    if (startZone) {
      firstNew = commandEndPoint(transformed.letter, transformed.args, previousNew, firstNew);
    }

    output.push(transformed);
    previous = commandEndPoint(segment.letter, segment.args, previous, first);
    previousNew = commandEndPoint(transformed.letter, transformed.args, previousNew, firstNew);
    startZone = isClose(segment.letter);
  }

  return serializeCommands(output);
}

/** Transform path `d` with a matrix in absolute coordinates (no relative round-trip). */
export function transformPathDataAbsolute(pathData: string, matrix: SvgMatrixLike): string {
  const commands = parsePathCommands(pathData);
  if (!commands.length) return '';

  const output: PathCommand[] = [];
  let previous: Point = { x: 0, y: 0 };
  let first: Point = { x: 0, y: 0 };

  for (const command of commands) {
    let segment = command;
    if (isHorzVert(segment.letter)) {
      segment = horzVertToLine(segment, previous);
    }

    const absolute = isRelative(segment.letter) ? toAbsolute(segment, previous) : segment;
    output.push(transformAbsoluteCommand(absolute, matrix));

    previous = commandEndPoint(segment.letter, segment.args, previous, first);
    if (isMove(segment.letter)) {
      first = previous;
    } else if (isClose(segment.letter)) {
      previous = first;
    }
  }

  return serializeCommands(output);
}

/** Shift path coordinates by (dx, dy) without relative round-trip (preserves Q/C length). */
export function translatePathData(pathData: string, dx: number, dy: number): string {
  if (!pathData?.trim() || (dx === 0 && dy === 0)) return pathData;
  return transformPathDataAbsolute(pathData, { a: 1, b: 0, c: 0, d: 1, e: dx, f: dy });
}
